import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  dataFieldsService,
  FieldType,
  MerchandisingRuleTypes,
  merchandisingRulesService,
  ShopDataField,
} from 'src/services';

type MerchandiseRule = MerchandisingRuleTypes.MerchandiseRule;
import {
  combineActionAndWeightText,
  getProductName,
} from 'src/components-bl/MerchandisingRules/merchandisingRules.helpers';
import { fetchAllMerchRules } from '../fetchAllMerchRules';
import {
  ConditionFieldOption,
  MerchRuleRow,
  MerchRulesEditorProps,
  NewRuleDraft,
  RuleEditDraft,
  UseMerchRulesEditorReturnType,
} from './types';

const DEFAULT_PAGE_LIMIT = 10;

// Reverts a Test rule's action/weight (and optionally active) back to whatever Control has,
// used whenever an edit is undone: toggling a row off, resetting one rule, or stripping them all.
function buildControlRevertPatch(
  control: MerchandiseRule,
  active: boolean
): Partial<MerchandiseRule> {
  return { active, action: control.action, weight: control.weight };
}

function buildRows(controlRules: MerchandiseRule[], testRules: MerchandiseRule[]): MerchRuleRow[] {
  const testById = new Map(testRules.map(rule => [rule.id, rule]));

  return controlRules.map(control => {
    const test = testById.get(control.id);

    if (!test) {
      return { control, test, state: 'unchanged' };
    }

    if (test.active !== control.active) {
      return { control, test, state: test.active === false ? 'disabled' : 'edited' };
    }

    const contentChanged = test.action !== control.action || test.weight !== control.weight;
    return { control, test, state: contentChanged ? 'edited' : 'unchanged' };
  });
}

export const useMerchRulesEditor = ({
  shopId,
  surface,
  controlVariantId,
  testVariantId,
  onChangeCountChange,
  onSaveReady,
}: MerchRulesEditorProps): UseMerchRulesEditorReturnType => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [controlRules, setControlRules] = useState<MerchandiseRule[]>([]);
  const [testRules, setTestRules] = useState<MerchandiseRule[]>([]);
  const [totalRulesCount, setTotalRulesCount] = useState(0);
  const [dataFields, setDataFields] = useState<ShopDataField[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingRowId, setEditingRowId] = useState<string | undefined>(undefined);
  const [pagination, setPagination] = useState({ skip: 0, limit: DEFAULT_PAGE_LIMIT });

  const loadRules = useCallback(
    (silent = false): Promise<void> => {
      if (!controlVariantId || !testVariantId) return Promise.resolve();

      if (!silent) setIsLoading(true);
      setHasError(false);
      return Promise.all([
        fetchAllMerchRules({ shopId, variantId: controlVariantId, product: [surface] }),
        fetchAllMerchRules({ shopId, variantId: testVariantId, product: [surface] }),
        merchandisingRulesService.getRules({
          shopId,
          variantId: controlVariantId,
          skip: 0,
          limit: 1,
        }),
        dataFieldsService.getDataFields({ shopId, fieldType: FieldType.CatalogField }),
      ])
        .then(([fetchedControlRules, fetchedTestRules, unfilteredResult, fetchedDataFields]) => {
          setControlRules(fetchedControlRules);
          setTestRules(fetchedTestRules);
          setTotalRulesCount(unfilteredResult.pagination.totalCount);
          setDataFields(fetchedDataFields);
        })
        .catch(() => {
          setControlRules([]);
          setTestRules([]);
          setTotalRulesCount(0);
          setDataFields([]);
          setHasError(true);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [shopId, surface, controlVariantId, testVariantId]
  );

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  const rows = useMemo(() => buildRows(controlRules, testRules), [controlRules, testRules]);

  // Test rules seeded from the control (existing rules for this surface) show up in `rows`;
  // anything left over is a rule the "New rule" composer created, straight on the Test variant.
  const newRules = useMemo(() => {
    const controlIds = new Set(controlRules.map(rule => rule.id));
    return testRules.filter(rule => !controlIds.has(rule.id));
  }, [controlRules, testRules]);

  const visibleRows = useMemo(() => {
    if (!searchTerm.trim()) return rows;

    const term = searchTerm.trim().toLowerCase();
    return rows.filter(row => {
      const controlText = combineActionAndWeightText(row.control).toLowerCase();
      const testText = row.test ? combineActionAndWeightText(row.test).toLowerCase() : '';
      return (
        row.control.name.toLowerCase().includes(term) ||
        controlText.includes(term) ||
        testText.includes(term)
      );
    });
  }, [rows, searchTerm]);

  useEffect(() => {
    setPagination(prev => (prev.skip === 0 ? prev : { ...prev, skip: 0 }));
  }, [searchTerm]);

  const pagedRows = useMemo(
    () => visibleRows.slice(pagination.skip, pagination.skip + pagination.limit),
    [visibleRows, pagination]
  );

  // Every toggle/edit/add/remove already writes straight to the Test variant's rules
  // (fire-and-forget from the row/drawer/composer's point of view), but a fast Continue click
  // right after an edit could race the in-flight request(s). `pendingOperationsRef` lets `save()`
  // wait for everything still in flight instead of navigating away before it lands — a single
  // "latest operation" ref would let an earlier, still-pending edit be dropped by a later one.
  const pendingOperationsRef = useRef<Set<Promise<void>>>(new Set());
  const trackPending = useCallback((operation: Promise<void>): Promise<void> => {
    pendingOperationsRef.current.add(operation);
    const untrack = (): void => {
      pendingOperationsRef.current.delete(operation);
    };
    operation.then(untrack, untrack);
    return operation;
  }, []);

  const editingRow = rows.find(row => row.control.id === editingRowId);

  const openEditDrawer = useCallback((row: MerchRuleRow) => setEditingRowId(row.control.id), []);
  const closeEditDrawer = useCallback(() => setEditingRowId(undefined), []);

  // Test copies of every control rule are seeded when the variant is created
  // (build-your-own draft or playbook application), so `test` should always
  // resolve — falling back to `control.id` only guards against that invariant
  // ever breaking rather than an expected runtime case.
  const patchTestRule = useCallback(
    (row: MerchRuleRow, patch: Partial<MerchandiseRule>): Promise<void> => {
      if (!testVariantId) return Promise.resolve();
      const id = row.test?.id ?? row.control.id;

      return merchandisingRulesService
        .update({ shopId, variantId: testVariantId, rule: { id, ...patch } })
        .then(updatedRule => {
          setTestRules(prev => prev.map(rule => (rule.id === id ? updatedRule : rule)));
        })
        .catch(() => setHasError(true));
    },
    [shopId, testVariantId]
  );

  const toggleRuleActive = useCallback(
    (row: MerchRuleRow, active: boolean): Promise<void> =>
      trackPending(
        patchTestRule(row, active ? { active } : buildControlRevertPatch(row.control, active))
      ),
    [patchTestRule, trackPending]
  );

  const saveRuleEdit = useCallback(
    (row: MerchRuleRow, draft: RuleEditDraft): Promise<void> =>
      trackPending(
        patchTestRule(row, {
          active: true,
          action: draft.action,
          weight: draft.weight,
          subRules: [
            {
              enabled: true,
              field: draft.field,
              fieldType: draft.fieldType,
              subType: draft.subType,
              values: draft.values,
              position: 0,
            },
          ],
        })
      ),
    [patchTestRule, trackPending]
  );

  const resetToControl = useCallback(
    (row: MerchRuleRow): Promise<void> =>
      trackPending(patchTestRule(row, buildControlRevertPatch(row.control, row.control.active))),
    [patchTestRule, trackPending]
  );

  // One bulk request instead of one PATCH per rule - with hundreds of rules, firing that many
  // concurrent PATCHes at the same variant's rule set was exactly the kind of overlapping-write
  // pattern that causes Mongo WriteConflicts (see the carousel view-config bug), just fanned out
  // across documents instead of one.
  const stripAllRules = useCallback((): Promise<void> => {
    if (!testVariantId) return Promise.resolve();

    return trackPending(
      merchandisingRulesService
        .bulkUpdate({
          shopId,
          variantId: testVariantId,
          rules: rows
            .filter(row => row.state !== 'disabled')
            .map(row => ({
              id: row.test?.id ?? row.control.id,
              active: false,
              action: row.control.action,
              weight: row.control.weight,
            })),
        })
        .then(() => loadRules(true))
        .then(() => undefined)
        .catch(() => setHasError(true))
    );
  }, [shopId, testVariantId, rows, loadRules, trackPending]);

  // The "New rule" composer only ever exposes action + one condition (KPI, schedule and context
  // conditions stay in Merchandising Rules) - everything else here mirrors the app's own new-rule
  // defaults (see MerchandisingRuleForm.config.ts `rulesFormInitialData`).
  const addRule = useCallback(
    (draft: NewRuleDraft): Promise<void> => {
      if (!testVariantId) return Promise.resolve();

      const rule: Omit<MerchandiseRule, 'id'> = {
        name: draft.name,
        active: true,
        action: draft.action,
        weight: draft.weight,
        product: surface as MerchandisingRuleTypes.MerchandisingRuleProduct,
        redirectRule: null,
        regions: [],
        appliedDateRange: null,
        searchCondition: { enabled: false, terms: [] },
        sourceCondition: [{ enabled: false, values: [], fieldType: FieldType.CatalogField }],
        subRules: [
          {
            enabled: true,
            field: draft.field,
            fieldType: draft.fieldType,
            subType: draft.subType,
            values: draft.values,
            position: 0,
          },
        ],
        context: [],
      };

      return trackPending(
        merchandisingRulesService
          .create({ shopId, variantId: testVariantId, rule })
          .then(createdRule => {
            setTestRules(prev => [...prev, createdRule]);
          })
          .catch(() => setHasError(true))
      );
    },
    [shopId, surface, testVariantId, trackPending]
  );

  const removeNewRule = useCallback(
    (rule: MerchandiseRule): Promise<void> => {
      if (!testVariantId) return Promise.resolve();

      return trackPending(
        merchandisingRulesService
          .delete({ shopId, ruleId: rule.id, variantId: testVariantId })
          .then(() => {
            setTestRules(prev => prev.filter(testRule => testRule.id !== rule.id));
          })
          .catch(() => setHasError(true))
      );
    },
    [shopId, testVariantId, trackPending]
  );

  const save = useCallback(
    (): Promise<boolean> => Promise.all(pendingOperationsRef.current).then(() => true),
    []
  );

  useEffect(() => {
    onSaveReady?.(save);
  }, [save, onSaveReady]);

  const disabledCount = rows.filter(row => row.state === 'disabled').length;
  const editedCount = rows.filter(row => row.state === 'edited').length;
  // Turning every existing rule off is the baseline playbook and counts as one semantic change,
  // regardless of how many rules that touches - it must never trip the blast-radius warning.
  const isBaseline =
    rows.length > 0 && disabledCount === rows.length && editedCount === 0 && newRules.length === 0;
  const changeCount = isBaseline ? 1 : disabledCount + editedCount + newRules.length;

  useEffect(() => {
    onChangeCountChange?.(changeCount);
  }, [changeCount, onChangeCountChange]);

  const surfaceLabel = getProductName(surface);
  const controlActiveCount = controlRules.filter(rule => rule.active).length;
  const otherSurfacesRulesCount = Math.max(0, totalRulesCount - controlRules.length);

  const controlItems = useMemo(
    () => [
      { label: `Rules on ${surfaceLabel}`, value: `${controlActiveCount} active` },
      { label: 'Other surfaces', value: `${otherSurfacesRulesCount} rules` },
      { label: 'Ranking weights', value: 'unchanged' },
    ],
    [surfaceLabel, controlActiveCount, otherSurfacesRulesCount]
  );

  const conditionFieldOptions: ConditionFieldOption[] = useMemo(
    () =>
      dataFields.map(field => ({
        value: field.name,
        text: field.displayName,
        types: field.types,
        fieldType: field.fieldType,
      })),
    [dataFields]
  );

  return {
    isLoading,
    hasError,
    rows,
    visibleRows,
    pagedRows,
    pagination,
    setPagination,
    searchTerm,
    setSearchTerm,
    totalRulesCount,
    surfaceRulesCount: controlRules.length,
    otherSurfacesRulesCount,
    surfaceLabel,
    controlItems,
    editingRow,
    openEditDrawer,
    closeEditDrawer,
    toggleRuleActive,
    saveRuleEdit,
    resetToControl,
    stripAllRules,
    isBaseline,
    changeCount,
    newRules,
    removeNewRule,
    addRule,
    conditionFieldOptions,
  };
};
